import React from 'react';

import { Box, Flex, Link, Text, useMediaQuery } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';

import { DOCS_BASE_DOMAIN } from '@/pages/merchant-pages/payment-widget/util';

import { WebPImage } from '@/components/WebPImage';

import { MediaQueryAmountsEnum } from '@/enum/mediaQueryAmounts.enum';

/**
 * Component for Footer section in Home page
 * @component
 * @example
 * <FooterSection />.
 */
const FooterSection: React.FC = () => {
  const { t } = useTranslation();
  const [isMobile] = useMediaQuery(MediaQueryAmountsEnum.isMobile);

  return (
    <Flex
      sx={{
        background: 'app.pink',
        h: { base: 'inherit', lg: '417px' },
        color: 'app.white',
        justifyContent: 'center',
      }}
    >
      <Flex
        align={{ base: 'flex-start', lg: 'center' }}
        direction={{ base: 'column', lg: 'row' }}
        justify='space-between'
        p={{ base: '25px 40px 200px', lg: '0 70px' }}
        w={{ base: '100%', lg: '1440px' }}
      >
        <Box m={{ base: '0 0 0 -40px', lg: '-50px 0 0 0' }}>
          <WebPImage
            alt='Remuno'
            commonPath='/images/footerLogo.png'
            webpPath='/images/webp/footerLogo.webp'
          />
        </Box>

        <Flex
          fontSize='17px'
          justify='space-between'
          sx={{
            '>div': {
              w: { base: '120px', lg: 'inherit' },
            },
          }}
          w={{ base: '100%', lg: '700px' }}
          wrap={{ base: 'wrap', lg: 'inherit' }}
        >
          <Box
            mb={{ base: '50px', md: '0' }}
            w={isMobile ? '50% !important' : 'auto'}
          >
            <Text fontSize='21px' fontWeight='bold'>
              {t('main.footer.remuno.title')}
            </Text>
            <Link
              as={ScrollLink}
              color='app.white'
              duration={500}
              smooth={true}
              spy={true}
              to='about-us'
            >
              {t('main.footer.remuno.aboutUs')}
            </Link>
            <br />
            <Link
              as={ScrollLink}
              color='app.white'
              duration={500}
              smooth={true}
              spy={true}
              to='management-team'
            >
              {t('main.footer.remuno.managementTeam')}
            </Link>
            <br />
          </Box>

          <Box w={isMobile ? '50% !important' : 'auto'}>
            <Text fontSize='21px' fontWeight='bold'>
              {t('main.footer.support.title')}
            </Text>

            <Link
              as={ScrollLink}
              color='app.white'
              duration={500}
              smooth={true}
              spy={true}
              to='faq-section'
            >
              {t('main.footer.support.faq')}
            </Link>
            <br />
            <Link color='app.white' href='mailto:support@remuno.com'>
              {t('main.footer.support.contactUs')}
            </Link>
          </Box>

          <Box w={isMobile ? '50% !important' : 'auto'}>
            <Text fontSize='21px' fontWeight='bold'>
              {t('main.footer.developers.title')}
            </Text>

            <Link
              color='app.white'
              href={`https://${DOCS_BASE_DOMAIN}`}
              rel='noopener noreferrer'
              target='_blank'
            >
              {t('main.footer.developers.api')}
            </Link>
          </Box>

          <Box w={isMobile ? '50% !important' : 'auto'}>
            <Text fontSize='21px' fontWeight='bold'>
              {t('main.footer.legal.title')}
            </Text>

            <Link
              as={RouterLink}
              color='app.white'
              rel='noopener noreferrer'
              target='_blank'
              to='/documents/privacyPolicy.pdf'
            >
              {t('main.footer.legal.privacyPolicy')}
            </Link>
            <br />
            <Link
              as={RouterLink}
              color='app.white'
              rel='noopener noreferrer'
              target='_blank'
              to='/documents/termsOfUse.pdf'
            >
              {t('main.footer.legal.userAgreement')}
            </Link>
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default FooterSection;
